/* eslint-disable react/jsx-no-duplicate-props */
import { faTint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, DialogActions, DialogContent, InputAdornment, TextField,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';

import {
  CustomTooltip,
  FlexContainer,
  FontIcon,
  ItemOption,
  ItemSelected,
  Question,
  RequiredSelectHack,
  Text
} from 'components';
import { useStores } from 'hooks';
import { BottleView, FluidManipulationEntryView } from 'models';
import { ManipulationCategory } from 'models/detail/FluidManipulationEntryView';
import React, {
  Fragment, useCallback, useEffect, useState
} from 'react';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { FluidsService } from 'services';
import styled from 'styled-components';
import { COUNTRY_CODE, FLUID_MANIPULATION_LIST_INPUT, FLUID_MANIPULATION_LIST_OUTPUT } from 'utils/constants';
import {
  BottleHelper, formatDateString, FormHelper, InterventionHelper, translate
} from 'utils/helpers';
import { SelectItem } from 'utils/types';

import { ModalHeader } from './ModalHeader';

const SelectGroupLabel = styled(FlexContainer).attrs({
  alignItems: 'center',
  justifyContent: 'space-between'
})`
  color: var(--turquoise);
  font-weight: 600;
`;

export const AddFluidManipulationModal = ({
                                            countryCode,
                                            currentEntry,
                                            expectedAmount,
                                            expectedFluid,
                                            fluidManipulationsInput = [],
                                            fluidManipulationsOutput = [],
                                            manipulationType,
                                            onClose,
                                            onSubmit
                                          }: {
  countryCode: string,
  currentEntry: FluidManipulationEntryView,
  expectedAmount: number,
  expectedFluid: SelectItem,
  fluidManipulationsInput: FluidManipulationEntryView[],
  fluidManipulationsOutput: FluidManipulationEntryView[],
  manipulationType: ManipulationCategory,
  onClose: (e?: any) => void,
  onSubmit: (manipulation: FluidManipulationEntryView) => void
}) => {
  const { userStore } = useStores();
  const alreadyAddedManipulations = manipulationType === 'output' ? fluidManipulationsOutput : fluidManipulationsInput;
  const fixedSelectedFluid = alreadyAddedManipulations.length > 0 ? alreadyAddedManipulations[0].fluid : undefined;

  const [fluidOptions, setFluidOptions] = useState([]);
  const [availableBottleOptions, setAvailableBottleOptions] = useState([]);
  const [isRetrievalThresholdReached, setIsRetrievalThresholdReached] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // React Hook Form setup
  const initialValues = {
    ...currentEntry,
    type: manipulationType,
    fluid: (currentEntry && currentEntry.fluid) || fixedSelectedFluid || expectedFluid
  };

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<FluidManipulationEntryView>({
    defaultValues: initialValues
  });

  // Watch values
  const manipulationTypeValue = watch('manipulationType');
  const fluidValue = watch('fluid');
  const fluidBottleValue = watch('fluidBottle');
  const fluidAmountValue = watch('fluidAmount');
  const bottleControlResultValue = watch('bottleControlResult');
  const fluidKeptByOwnerValue = watch('fluidKeptByOwner');

  const isCanada = countryCode === COUNTRY_CODE.CA;

  const FLUID_MANIPULATION_LIST_INPUT_TRANSLATED = FLUID_MANIPULATION_LIST_INPUT.map(manip => ({
    ...manip,
    label: translate(manip.label)
  }));

  const FLUID_MANIPULATION_LIST_OUTPUT_TRANSLATED = FLUID_MANIPULATION_LIST_OUTPUT.map(manip => ({
    ...manip,
    label: translate(manip.label)
  }));

  const manipulationTypeData = {
    input: {
      title: translate('modalFluidManipulation.inputTitle'),
      questionLabel: translate('modalFluidManipulation.inputQuestionLabel'),
      options: FLUID_MANIPULATION_LIST_INPUT_TRANSLATED,
      amountLabel: translate('modalFluidManipulation.inputAmountLabel')
    },
    output: {
      title: translate('modalFluidManipulation.outputTitle'),
      questionLabel: translate('modalFluidManipulation.outputQuestionLabel'),
      options: FLUID_MANIPULATION_LIST_OUTPUT_TRANSLATED,
      amountLabel: translate('modalFluidManipulation.outputAmountLabel')
    }
  };

  const completeBottleList = useCallback(({ bottles, addMapping = false }: {addMapping?: boolean, bottles: BottleView[]}) => {
    let completeBottles = bottles;
    if (addMapping) {
      completeBottles = bottles.map(bottle => ({
        ...bottle,
        value: bottle.id,
        label: bottle.identifier
      }));
    }

    // Filter bottles to avoid two movements of same type with the same bottle
    setAvailableBottleOptions(completeBottles.filter((b) => {
      switch (manipulationTypeValue?.value) {
        case 'FLUID_FOR_TREATMENT_OUTPUT':
        case 'FLUID_FOR_REUSE_OUTPUT':
          return currentEntry?.fluidBottle?.id === b.id || !fluidManipulationsOutput.find(fm => fm.fluidBottle.id === b.id);
        case 'NEW_FLUID_INPUT':
        case 'RECYCLED_FLUID_INPUT':
        case 'REGENERATED_FLUID_INPUT':
          return currentEntry?.fluidBottle?.id === b.id || !fluidManipulationsInput.find(fm => fm.fluidBottle.id === b.id);
        default:
          return false;
      }
    }));
  }, [currentEntry, fluidManipulationsOutput, fluidManipulationsInput, manipulationTypeValue?.value]); // ✅ Dépendance spécifique

  useEffect(() => {
    if (userStore.isOffline) {
      InterventionHelper.loadFluidsStored()
          .then(fluids => setFluidOptions(fluids));
    } else {
      FluidsService.getFluidOptions()
          .then(fluids => setFluidOptions(fluids));
    }
  }, [userStore.isOffline]);

  // Fetch the bottles corresponding to the manipulation type and the equipment fluid
  useEffect(() => {
    if (manipulationTypeValue && fluidValue) {
      if (userStore.isOffline) {
        InterventionHelper.loadBottlesStored()
            .then((bottles) => {
              // Filter bottles on manipulationType & fluidHashId on frontend
              const bottlesFiltered = bottles
                  .filter(bottle => bottle.fluid?.value === fluidValue?.value)
                  .filter((bottle) => {
                    switch (manipulationTypeValue?.value) {
                      case 'FLUID_FOR_TREATMENT_OUTPUT':
                        return bottle.bottleType?.value === 'RETRIEVAL_BOTTLE';
                      case 'FLUID_FOR_REUSE_OUTPUT':
                        return bottle.bottleType?.value === 'RETRIEVAL_BOTTLE' || bottle.bottleType?.value === 'TRANSFER_BOTTLE';
                      case 'NEW_FLUID_INPUT':
                        return bottle.bottleType?.value === 'NEW_BOTTLE';
                      case 'RECYCLED_FLUID_INPUT':
                        return bottle.bottleType?.value === 'RETRIEVAL_BOTTLE' || bottle.bottleType?.value === 'TRANSFER_BOTTLE';
                      case 'REGENERATED_FLUID_INPUT':
                        return bottle.bottleType?.value === 'NEW_BOTTLE';
                      default:
                        return bottle;
                    }
                  });

              completeBottleList({ bottles: bottlesFiltered });
            });
      } else if (manipulationTypeValue?.value && fluidValue?.value) {
          FluidsService.getBottleOptionsExtended({
            manipulationType: FormHelper.SELECT_ADAPTOR_TO_BACK(manipulationTypeValue),
            fluidId: fluidValue.value
          }).then(bottles => completeBottleList({ bottles, addMapping: true }));
        }
    } else {
      setAvailableBottleOptions([]);
    }
  }, [userStore.isOffline, manipulationTypeValue?.value, fluidValue?.value]); // ✅ Dépendances spécifiques

  useEffect(() => {
    const shouldPrintThresholdWarningCapacity = (fluidAmount: number) => {
      const { maximumCapacity, bottleType, theoricalAmount } = fluidBottleValue || {};
      if (!maximumCapacity || bottleType?.key !== 'RETRIEVAL_BOTTLE') {
        return false;
      }
      const thresholdCapacity = (maximumCapacity * 0.8);
      const totalAmount = (theoricalAmount || 0) + fluidAmount;
      return totalAmount >= thresholdCapacity;
    };

    if (fluidBottleValue) {
      setIsRetrievalThresholdReached(shouldPrintThresholdWarningCapacity(Number(fluidAmountValue)));
    }
  }, [fluidAmountValue, fluidBottleValue]);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    // When adding a fluid output, try to prefill some fields
    if (manipulationType === 'output' && !currentEntry) {
      if (fluidManipulationsOutput.length === 0) {
        // Prefill the fluid and fluid amount
        setValue('fluid', fixedSelectedFluid || expectedFluid);
        setValue('fluidAmount', expectedAmount);
      } else {
        // Prefill the fluid
        setValue('fluid', fluidManipulationsOutput[0].fluid);
      }
      if (isCanada) {
        setValue('bottleControlResult', 'SUCCESS');
        setValue('fluidKeptByOwner', false);
      }
    }

    // When adding a fluid input, try using the fluid for reuse output to prefill some fields
    if (manipulationType === 'input' && !currentEntry) {
      const fluidForReuseOutputs = fluidManipulationsOutput.filter(manip => manip.manipulationType.value === 'FLUID_FOR_REUSE_OUTPUT');
      if (fluidForReuseOutputs.length === 1 && fluidManipulationsInput.length === 0) {
        // Prefill the amount with the output amount
        setValue('fluidAmount', fluidForReuseOutputs[0].fluidAmount);
        const recycledFluidInputs = fluidManipulationsOutput.filter(manip => manip.manipulationType.value === 'RECYCLED_FLUID_INPUT');
        if (recycledFluidInputs.length === 0) {
          // Prefill the type, fluid and fluid bottle
          setValue('manipulationType', FLUID_MANIPULATION_LIST_INPUT_TRANSLATED.find(manip => manip.value === 'RECYCLED_FLUID_INPUT'));
          setValue('fluid', fluidForReuseOutputs[0].fluid);
          setValue('fluidBottle', fluidForReuseOutputs[0].fluidBottle);
        }
      } else {
        // Prefill the amount with the equipment capacity
        setValue('fluidAmount', expectedAmount);
      }
    }
    setIsInitialized(true);
  }, [
    isInitialized,
    manipulationType,
    currentEntry,
    fluidManipulationsOutput,
    fluidManipulationsInput,
    fixedSelectedFluid,
    expectedFluid,
    expectedAmount,
    isCanada,
    setValue,
    FLUID_MANIPULATION_LIST_INPUT_TRANSLATED
  ]);

  const handleValidateModalSubmit = (data) => {
    onSubmit({
      ...data,
      fluidAmount: Number(data.fluidAmount)
    });
  };

  const formatGroupLabel = data => (
    <SelectGroupLabel>
      <span>{data.label}</span>
    </SelectGroupLabel>
  );

  const CustomOption = ({ data, innerProps }) => (
    <ItemOption {...innerProps} isInsideSelect>
      <article>
        {BottleHelper.getTranslatedType({
            type: data.bottleType.key,
            isNewFluid: data.isNewFluid,
            identifier: data.identifier
          })}
      </article>
    </ItemOption>
  );

  return (
    <Fragment>
      <form onSubmit={handleSubmit(handleValidateModalSubmit)}>
        <ModalHeader onClose={onClose}>
          <FontAwesomeIcon icon={faTint} />
          {currentEntry ? translate('modalFluidManipulation.editManipulation') : manipulationTypeData[manipulationType].title}
        </ModalHeader>

        <DialogContent style={{ minHeight: '360px', padding: '10px' }}>
          <FlexContainer flexDirection="column">
            <Question label={manipulationTypeData[manipulationType].questionLabel}>
              <Controller
                    control={control}
                    name="manipulationType"
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                      <Select
                            {...field}
                            formatGroupLabel={formatGroupLabel}
                            id="selectManipulationType"
                            isClearable
                            menuPortalTarget={document.body}
                            noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                            options={manipulationTypeData[manipulationType].options}
                            placeholder={translate('modalFluidManipulation.selectManipulation')}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                            value={value}
                            onChange={(selectedValue) => {
                              onChange(selectedValue);
                              setValue('fluidBottle', null);
                            }}
                        />
                    )}
                    rules={{ required: 'Ce champ est requis' }}
                />
              <RequiredSelectHack value={manipulationTypeValue || ''} />
            </Question>

            {manipulationTypeValue && (
              <Fragment>
                <Question label={translate('modalFluidManipulation.fluidReference')}>
                  <Controller
                          control={control}
                          name="fluid"
                          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                            <Select
                                  {...field}
                                  id="selectFluid"
                                  isClearable
                                  isDisabled={!!fixedSelectedFluid}
                                  menuPortalTarget={document.body}
                                  noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                  options={fluidOptions}
                                  placeholder={translate('modalFluidManipulation.fluidReference')}
                                  styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                  value={value}
                                  onChange={(selectedValue) => {
                                    onChange(selectedValue);
                                    setValue('fluidBottle', null);
                                  }}
                              />
                          )}
                          rules={{ required: 'Ce champ est requis' }}
                      />
                  <RequiredSelectHack value={fluidValue || ''} />
                </Question>

                <Question
                        icon={isRetrievalThresholdReached ? (
                          <CustomTooltip
                                size="xs"
                                text={translate('warnings.bottle.retrievalBottleThreshold')}
                            />
                        ) : null}
                        label={translate('modalFluidManipulation.bottleUsed')}
                    >
                  {!fluidBottleValue ? (
                    <div>
                      <Select
                                components={{ Option: CustomOption }}
                                id="selectFluidBottle"
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                options={availableBottleOptions}
                                placeholder={translate('modalFluidManipulation.selectBottleUsed')}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 3000 }) }}
                                value={fluidBottleValue || null}
                                onChange={(selectedValue) => {
                                  setValue('fluidBottle', selectedValue);
                                }}
                            />
                      <RequiredSelectHack value={fluidBottleValue || ''} />
                    </div>
                      ) : (
                        <ItemSelected
                              deleteAction={() => {
                                setValue('fluidBottle', null);
                              }}
                          >
                          <ItemOption>
                            <FontIcon icon="icon-container" />
                            <article>
                              <Text fontSize="1.8rem" margin="0">{fluidBottleValue.identifier}</Text>
                              <Text color="var(--grey)" fontSize="1.4rem" margin="0">
                                {`${translate('common.quantity')} : ${fluidBottleValue.measuredAmount}${translate('common.weightUnit')}`}
                              </Text>
                              <Text color="var(--grey)" fontSize="1.4rem" margin="0">{formatDateString(fluidBottleValue.receptionDate)}</Text>
                            </article>
                          </ItemOption>
                        </ItemSelected>
                      )}
                </Question>

                <FlexContainer alignItems="center" justifyContent="space-between" style={{ minWidth: '80%' }}>
                  <Controller
                          control={control}
                          name="fluidAmount"
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                                  {...field}
                                  error={!!error}
                                  helperText={error?.message}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                  }}
                                  inputProps={{
                                    step: '0.01',
                                    min: 0.01
                                  }}
                                  label={manipulationTypeData[manipulationType].amountLabel}
                                  required
                                  style={{ minWidth: '80%' }}
                                  type="number"
                              />
                          )}
                          rules={{ required: 'Ce champ est requis' }}
                      />
                  <CustomTooltip
                          text={translate('modalFluidManipulation.warningFluidAmount', { fluidAmount: expectedAmount })}
                      />
                </FlexContainer>

                {manipulationType === 'output' && isCanada && (
                <Question label={translate('modalFluidManipulation.bottleControlResult')}>
                  <ToggleButtonGroup
                              aria-label={translate('modalFluidManipulation.bottleControlResult')}
                              exclusive
                              size="medium"
                              style={{ marginBottom: '1rem' }}
                              value={bottleControlResultValue}
                              onChange={(event, value) => {
                                if (value === null) return;
                                setValue('bottleControlResult', value);
                              }}
                          >
                    <ToggleButton
                                aria-label={translate('print.enum.BottleControlResult.SUCCESS')}
                                value='SUCCESS'
                            >
                      {translate('print.enum.BottleControlResult.SUCCESS')}
                    </ToggleButton>
                    <ToggleButton
                                aria-label={translate('print.enum.BottleControlResult.FAILURE')}
                                value='FAILURE'
                            >
                      {translate('print.enum.BottleControlResult.FAILURE')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Question>
                    )}

                {manipulationType === 'output' && isCanada && (
                <Question label={translate('modalFluidManipulation.fluidKeptByOwner')}>
                  <ToggleButtonGroup
                              aria-label={translate('modalFluidManipulation.fluidKeptByOwner')}
                              exclusive
                              size="medium"
                              style={{ marginBottom: '1rem' }}
                              value={fluidKeptByOwnerValue}
                              onChange={(event, value) => {
                                if (value === null) return;
                                setValue('fluidKeptByOwner', value);
                              }}
                          >
                    <ToggleButton
                                aria-label={translate('common.yes')}
                                value
                            >
                      {translate('common.yes')}
                    </ToggleButton>
                    <ToggleButton
                                aria-label={translate('common.no')}
                                value={false}
                            >
                      {translate('common.no')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Question>
                    )}
              </Fragment>
              )}
          </FlexContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{translate('button.cancel')}</Button>
          <Button color="primary" type="submit">{translate('button.save')}</Button>
        </DialogActions>
      </form>
    </Fragment>
  );
};
